import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Smartphone, Building2, Copy, Save, Send, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function ChannelSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [provider, setProvider] = useState("baileys");
  const [apiKey, setApiKey] = useState(""); // blank = keep stored
  const [apiKeyMasked, setApiKeyMasked] = useState(null);
  const [instanceNumber, setInstanceNumber] = useState("");
  const [webhookUrl, setWebhookUrl] = useState(null);

  useEffect(() => {
    api
      .get("/channels/settings")
      .then(({ data }) => {
        setProvider(data.provider);
        setApiKeyMasked(data.jalpi_api_key_masked);
        setInstanceNumber(data.jalpi_instance_number || "");
        setWebhookUrl(data.webhook_url);
      })
      .catch(() => toast.error("Could not load channel settings"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/channels/settings", {
        provider,
        jalpi_api_key: apiKey || null,
        jalpi_instance_number: instanceNumber || null,
      });
      setWebhookUrl(data.webhook_url);
      setApiKeyMasked(data.jalpi_api_key_masked);
      setApiKey("");
      if (data.provider === "jalpi" && data.webhook_error)
        toast.warning(`Saved, but webhook registration failed: ${data.webhook_error}`);
      else toast.success("Channel settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const testKey = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/channels/jalpi/test", { jalpi_api_key: apiKey || null });
      toast.success(`Key valid${data.templates_count != null ? ` — ${data.templates_count} templates` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Key test failed");
    } finally {
      setTesting(false);
    }
  };

  const copyWebhook = async () => {
    if (!webhookUrl) return;
    await navigator.clipboard.writeText(webhookUrl);
    toast.success("Webhook URL copied");
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <Link
        to="/app/settings"
        className="inline-flex items-center gap-1 text-[12.5px] text-slate-500 hover:text-slate-900 mb-3"
      >
        <ChevronLeft size={14} /> Settings
      </Link>
      <div className="mb-6">
        <h1 className="text-[32px] font-extrabold tracking-tight text-slate-900 leading-tight">WhatsApp Channel</h1>
        <p className="text-[14px] text-slate-500 mt-1">
          Choose how WhatsApp connects: QR pairing (WhatsApp Web) or the official Business API (Jalpi).
        </p>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm py-6 text-center">Loading…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="grid sm:grid-cols-2 gap-3">
            <button
              onClick={() => setProvider("baileys")}
              className={
                "text-left p-4 rounded-lg border transition " +
                (provider === "baileys" ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-400")
              }
            >
              <div className="flex items-center gap-2 mb-1">
                <Smartphone size={16} className="text-blue-600" />
                <div className="text-[13.5px] font-bold text-slate-900">WhatsApp Web (QR pairing)</div>
              </div>
              <div className="text-[12px] text-slate-500">
                Pair a phone via QR code (Baileys). Unofficial method — fastest to set up, but not authorised by
                WhatsApp/Meta.
              </div>
            </button>

            <button
              onClick={() => setProvider("jalpi")}
              className={
                "text-left p-4 rounded-lg border transition " +
                (provider === "jalpi" ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-400")
              }
            >
              <div className="flex items-center gap-2 mb-1">
                <Building2 size={16} className="text-emerald-600" />
                <div className="text-[13.5px] font-bold text-slate-900">WhatsApp Business API (Jalpi)</div>
              </div>
              <div className="text-[12px] text-slate-500">
                Official API via Jalpi co-existence. Requires a Jalpi account and API key.
              </div>
            </button>
          </div>

          {provider === "jalpi" && (
            <div className="mt-5 pt-5 border-t border-slate-100 space-y-4">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5 block">
                  Jalpi API Key
                </label>
                <input
                  type="password"
                  placeholder={apiKeyMasked ? apiKeyMasked : "Enter your Jalpi API key"}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-[13px] focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition font-mono"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  {apiKeyMasked
                    ? "Leave blank to keep the currently stored key."
                    : "No key stored yet — enter your Jalpi API key."}
                </p>
              </div>

              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5 block">
                  Instance Number (optional)
                </label>
                <input
                  type="text"
                  placeholder="Your WABA number with country code, e.g. 919876543210"
                  value={instanceNumber}
                  onChange={(e) => setInstanceNumber(e.target.value)}
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-[13px] focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition font-mono"
                />
              </div>

              <div>
                <button
                  onClick={testKey}
                  disabled={testing}
                  className="h-9 px-4 inline-flex items-center gap-1.5 border border-slate-200 hover:border-slate-900 rounded-lg text-[12.5px] font-medium transition disabled:opacity-60"
                >
                  {testing ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                  {testing ? "Testing…" : "Test key"}
                </button>
              </div>

              {webhookUrl && (
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5 block">
                    Webhook URL
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={webhookUrl}
                      className="flex-1 h-10 px-3 border border-slate-200 rounded-lg text-[13px] font-mono bg-slate-50 text-slate-600"
                    />
                    <button
                      onClick={copyWebhook}
                      className="h-10 px-3 inline-flex items-center gap-1.5 border border-slate-200 hover:border-slate-900 rounded-lg text-[12.5px] font-medium transition"
                    >
                      <Copy size={13} /> Copy
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">Registered automatically on save.</p>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-5 mt-5 border-t border-slate-100">
            <button
              onClick={save}
              disabled={saving}
              className="h-10 px-5 inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[13px] font-semibold transition disabled:opacity-60 active:scale-95"
            >
              <Save size={14} /> {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
